import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material'
import type { Class, ClassGroup } from '@timetable/types'

interface ClassFormDialogProps {
  open: boolean
  classItem: Class | null
  existingClasses: Class[]
  classGroups: ClassGroup[]
  onClose: () => void
  onSubmit: (data: { class_name: string; class_group: string; defaultSectionName?: string }) => void
}

export const ClassFormDialog: React.FC<ClassFormDialogProps> = ({
  open,
  classItem,
  existingClasses,
  classGroups,
  onClose,
  onSubmit,
}) => {
  const [className, setClassName] = React.useState('')
  const [classGroup, setClassGroup] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  
  const [createDefaultSection, setCreateDefaultSection] = React.useState(true)
  const [defaultSectionName, setDefaultSectionName] = React.useState('A')

  React.useEffect(() => {
    if (classItem) {
      setClassName(classItem.class_name)
      setClassGroup(classItem.class_group)
    } else {
      setClassName('')
      setClassGroup('')
      setCreateDefaultSection(true)
      setDefaultSectionName('A')
    }
    setError(null)
  }, [classItem, open])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedClassName = className.trim()
    if (!trimmedClassName) {
      setError('Class name is required')
      return
    }

    if (!classGroup) {
      setError('Class group is required')
      return
    }

    const trimmedSectionName = defaultSectionName.trim()
    if (!classItem && createDefaultSection && !trimmedSectionName) {
      setError('Default section name is required')
      return
    }

    const isDuplicate = existingClasses.some(
      (c) =>
        c.class_name.toLowerCase() === trimmedClassName.toLowerCase() &&
        c.id !== classItem?.id
    )

    if (isDuplicate) {
      setError(`Class "${trimmedClassName}" already exists`)
      return
    }

    onSubmit({
      class_name: trimmedClassName,
      class_group: classGroup,
      ...(!classItem && createDefaultSection ? { defaultSectionName: trimmedSectionName } : {}),
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {classItem ? 'Edit Class' : 'Add New Class'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Class Name"
              value={className}
              onChange={(e) => setClassName(e.target.value)}
              required
              fullWidth
              autoFocus
              placeholder="e.g., Class I, Class II, Grade 1"
              helperText="Enter the class/grade name"
            />

            <FormControl fullWidth required>
              <InputLabel id="class-group-label">Class Group</InputLabel>
              <Select
                labelId="class-group-label"
                value={classGroup}
                onChange={(e) => setClassGroup(e.target.value)}
                label="Class Group"
              >
                {classGroups.map((group) => (
                  <MenuItem key={group.id} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!classItem && (
              <Box sx={{ mt: 1, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={createDefaultSection}
                      onChange={(e) => setCreateDefaultSection(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Create a default section for this class"
                />
                {createDefaultSection && (
                  <TextField
                    label="Default Section Name"
                    value={defaultSectionName}
                    onChange={(e) => setDefaultSectionName(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    sx={{ mt: 2 }}
                    placeholder="e.g., A, Alpha, Red"
                  />
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {classItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
